import React, { useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, FormikProps } from 'formik';
import { Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { RootState } from 'slices';
import { AdjustPointsFormType } from 'types/customer/customerTypes';
import { putAdjustCustomerPoints } from 'services/customerService';
import {
  setActiveCustomerId,
  setAdjustPointsModalOpen,
} from 'slices/customer/reducer';
import { validationSchema } from './validationSchema';
import AdjustPointsForm from './AdjustPointForm';

const AdjustPointsModal = ({
  fetchPoints,
}: {
  fetchPoints: (personId: number) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<AdjustPointsFormType>>(null);

  const {
    adjustPointsModalOpen,
    activeCustomerId,
    AdjustPointsValues,
    countSubmit,
  } = useSelector((state: RootState) => state.Customer);

  const toggleModal = useCallback(
    (id: string | null = null) => {
      dispatch(setAdjustPointsModalOpen(!adjustPointsModalOpen));
      dispatch(setActiveCustomerId(id));
    },
    [adjustPointsModalOpen, dispatch]
  );

  const closeModal = () => toggleModal(null);

  const submitForm = async (values: AdjustPointsFormType) => {
    try {
      if (!activeCustomerId) {
        throw new Error('Customer id is missing');
      }

      const point = Number(values.point);
      if (!Number.isFinite(point) || point <= 0) {
        throw new Error(t('Points must be greater than zero'));
      }

      await putAdjustCustomerPoints(Number(activeCustomerId), {
        point_type: values.type,
        point,
        comments: values.comments.trim(),
      });

      toast.success(t('Points adjusted successfully'), { autoClose: 3000 });
      fetchPoints(Number(activeCustomerId));
      closeModal();
    } catch (e: any) {
      toast.error(e.message || 'Unexpected error', { autoClose: 3000 });
    }
  };

  useEffect(() => {
    if (formikRef.current?.errors) {
      const errorKeys = Object.keys(formikRef.current.errors);
      const firstErrorField = document.querySelector(
        `[name="${errorKeys[0]}"]`
      ) as HTMLInputElement | null;
      if (firstErrorField) firstErrorField.focus();
    }
  }, [formikRef, countSubmit]);

  return (
    <div className="page-content">
      <Container fluid>
        <Modal
          size="md"
          isOpen={adjustPointsModalOpen}
          toggle={closeModal}
          backdrop="static"
          centered
        >
          <ModalHeader className="bg-light p-3" toggle={closeModal}>
            {t('Adjust Points')}
          </ModalHeader>
          <ModalBody>
            <Formik
              initialValues={AdjustPointsValues}
              validationSchema={validationSchema(t)}
              onSubmit={submitForm}
              enableReinitialize
              innerRef={formikRef}
            >
              {(formik) => (
                <form noValidate onSubmit={formik.handleSubmit}>
                  <AdjustPointsForm formik={formik} />
                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <button type="submit" className="btn btn-success">
                      {t('Save')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </ModalBody>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default AdjustPointsModal;
